import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MicrosoftAzureService } from '../../services/microsoft-azure.service';
import { ToastService } from '../../../@core/services/toast.service';

/**
 * Microsoft OAuth Callback Component for Organization Consent
 * Handles ONLY organization-level Azure AD integration (PKCE flow)
 * This is separate from user login auth which is handled by LogInComponent
 * Route: /oauth/microsoft/organization/callback
 */
@Component({
    selector: 'app-microsoft-oauth-callback',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="flex items-center justify-center min-h-screen bg-gray-50">
            <div class="text-center">
                @if (isProcessing) {
                    <div class="flex flex-col items-center space-y-4">
                        <!-- Loading Spinner -->
                        <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
                        <h2 class="text-xl font-semibold text-gray-700">Processing Microsoft Azure consent...</h2>
                        <p class="text-gray-500">Please wait while we complete the setup.</p>
                    </div>
                } @else if (hasError) {
                    <div class="flex flex-col items-center space-y-4">
                        <!-- Error Icon -->
                        <div class="rounded-full bg-red-100 p-4">
                            <svg class="h-16 w-16 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 class="text-xl font-semibold text-gray-700">Failed to connect Microsoft Azure</h2>
                        <p class="text-gray-500">{{ errorMessage }}</p>
                        <button 
                            (click)="closeWindow()"
                            class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            Close
                        </button>
                    </div>
                } @else {
                    <div class="flex flex-col items-center space-y-4">
                        <!-- Success Icon -->
                        <div class="rounded-full bg-green-100 p-4">
                            <svg class="h-16 w-16 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 class="text-xl font-semibold text-gray-700">Successfully connected!</h2>
                        <p class="text-gray-500">Microsoft Azure has been connected to your organization.</p>
                        <p class="text-sm text-gray-400">{{ isPopup ? 'This window will close automatically...' : 'Redirecting...' }}</p>
                    </div>
                }
            </div>
        </div>
    `,
    styles: []
})
export class MicrosoftOauthCallbackComponent implements OnInit {
    /**
     * Processing state - true while exchanging code for tokens
     */
    isProcessing: boolean = true;

    /**
     * Error state - true if token exchange failed
     */
    hasError: boolean = false;

    /**
     * Error message to display
     */
    errorMessage: string = '';

    /**
     * Whether this is opened in a popup window
     */
    isPopup: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private microsoftAzureService: MicrosoftAzureService,
        private toast: ToastService
    ) { }

    ngOnInit() {
        // Check if this is a popup window
        this.isPopup = !!window.opener;

        // Extract OAuth parameters from URL
        const code = this.route.snapshot.queryParamMap.get('code');
        const state = this.route.snapshot.queryParamMap.get('state'); // organizationId
        const error = this.route.snapshot.queryParamMap.get('error');
        const errorDescription = this.route.snapshot.queryParamMap.get('error_description');

        // Check for OAuth errors
        if (error) {
            this.handleError(`OAuth error: ${errorDescription || error}`);
            return;
        }

        // Validate required parameters
        if (!code || !state) {
            this.handleError('Missing authorization code or organization ID');
            return;
        }

        // Handle organization-level consent
        this.exchangeCodeForToken(code, state);
    }

    /**
     * Exchange authorization code for access token and update organization
     * @param code - Authorization code from OAuth redirect
     * @param state - Organization ID from OAuth redirect
     */
    private exchangeCodeForToken(code: string, state: string) {
        // Construct the same callback URL that was used in the consent request
        const frontendCallbackUrl = `${window.location.origin}/oauth/microsoft/organization/callback`;


        // Call backend to exchange code for tokens and update organization details
        this.microsoftAzureService.exchangeCodeForToken(code, state, frontendCallbackUrl).subscribe({
            next: (response) => {
                if (response.success) {
                    this.isProcessing = false;

                    // Store success status for popup polling (fallback)
                    sessionStorage.setItem('microsoftConsentStatus', 'success');

                    // Send postMessage to parent window if opened in popup
                    if (this.isPopup && window.opener) {
                        try {
                            window.opener.postMessage(
                                { type: 'MICROSOFT_CONSENT_SUCCESS' },
                                window.location.origin
                            );
                        } catch (e) {
                            console.warn('Failed to send postMessage to parent:', e);
                        }
                    }

                    // Show success toast
                    this.toast.success('Microsoft Azure connected successfully!');

                    if (this.isPopup) {
                        // Close popup window after 2 seconds
                        setTimeout(() => {
                            window.close();
                        }, 2000);
                    } else {
                        // Navigate to integrations page
                        setTimeout(() => {
                            this.router.navigate(['/settings/integrations']);
                        }, 2000);
                    }
                } else {
                    this.handleError(response.message || 'Failed to exchange authorization code');
                }
            },
            error: (error) => {
                this.handleError(error.error?.message || 'Failed to connect Microsoft Azure');
            }
        });
    }


    /**
     * Handle errors during OAuth flow
     * @param message - Error message to display
     */
    private handleError(message: string) {
        this.isProcessing = false;
        this.hasError = true;
        this.errorMessage = message;

        // Clear success status
        sessionStorage.removeItem('microsoftConsentStatus');

        // Send postMessage to parent window if opened in popup
        if (this.isPopup && window.opener) {
            try {
                window.opener.postMessage(
                    { type: 'MICROSOFT_CONSENT_ERROR', message: message },
                    window.location.origin
                );
            } catch (e) {
                console.warn('Failed to send error postMessage to parent:', e);
            }
        }

        // Show error toast
        this.toast.error(message);

        // Auto-close after 5 seconds
        setTimeout(() => {
            this.closeWindow();
        }, 5000);
    }

    /**
     * Close the popup window or navigate to integrations page
     */
    closeWindow() {
        // If opened in popup, close it
        if (this.isPopup) {
            window.close();
        } else {
            // Navigate to integrations page
            this.router.navigate(['/settings/integrations']);
        }
    }
}

